import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import { availableMaps } from '../../config/maps';

/**
 * Map Selector Panel
 * 
 * Allows switching between available maps in the maps folder.
 */
@customElement('map-selector-panel')
export class MapSelectorPanel extends LitElement {
  @state() private currentMapPath = 'assets/maps/tooling-test.glb';
  @state() private currentMapIndex = 0;

  static styles = css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }

    .map-selector {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .map-display {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 6px;
    }

    .map-controls {
      display: flex;
      gap: 8px;
      align-items: stretch;
    }

    .map-name {
      flex: 1;
      padding: 10px 12px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
      font-family: 'Consolas', 'Monaco', monospace;
      font-size: 12px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .map-filename {
      font-weight: 600;
    }

    .map-counter {
      opacity: 0.6;
      font-size: 11px;
    }

    button {
      padding: 8px 16px;
      background: rgba(100, 100, 255, 0.3);
      border: 1px solid rgba(100, 100, 255, 0.5);
      border-radius: 4px;
      color: white;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
      transition: all 0.15s;
      min-width: 40px;
    }

    button:hover {
      background: rgba(100, 100, 255, 0.5);
      border-color: rgba(100, 100, 255, 0.7);
    }

    button:active {
      transform: translateY(1px);
    }

    button.reload {
      background: rgba(100, 200, 100, 0.3);
      border-color: rgba(100, 200, 100, 0.5);
      font-size: 16px;
      padding: 8px 12px;
    }

    button.reload:hover {
      background: rgba(100, 200, 100, 0.5);
      border-color: rgba(100, 200, 100, 0.7);
    }

    .no-maps {
      padding: 16px;
      text-align: center;
      opacity: 0.6;
      font-style: italic;
    }

    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
      color: rgba(255, 255, 255, 0.9);
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    
    // Initialize current map index based on current path
    const index = availableMaps.findIndex(m => m.path === this.currentMapPath);
    if (index !== -1) {
      this.currentMapIndex = index;
    }
  }

  private previousMap() {
    if (availableMaps.length === 0) return;
    this.currentMapIndex = (this.currentMapIndex - 1 + availableMaps.length) % availableMaps.length;
    this.changeMap();
  }

  private nextMap() {
    if (availableMaps.length === 0) return;
    this.currentMapIndex = (this.currentMapIndex + 1) % availableMaps.length;
    this.changeMap();
  }

  private reloadMap() {
    if (availableMaps.length === 0) return;
    this.changeMap();
  }

  private changeMap() {
    const map = availableMaps[this.currentMapIndex];
    this.currentMapPath = map.path;
    
    // Dispatch command to bridge for map change
    DebugUiBridge.dispatchCommand({ 
      type: 'map:change', 
      path: map.path 
    });
  }

  render() {
    if (availableMaps.length === 0) {
      return html`
        <div class="map-selector">
          <h3>🗺️ Map Selector</h3>
          <div class="no-maps">No maps found in assets/maps/</div>
        </div>
      `;
    }

    const currentMap = availableMaps[this.currentMapIndex];

    return html`
      <div class="map-selector">
        <h3>🗺️ Map Selector</h3>
        
        <div class="map-display">
          <div class="map-name">
            <div class="map-filename">${currentMap.filename}</div>
            <div class="map-counter">${this.currentMapIndex + 1} / ${availableMaps.length}</div>
          </div>
        </div>

        <div class="map-controls">
          <button @click=${this.previousMap} title="Previous map">◀</button>
          <button @click=${this.nextMap} title="Next map">▶</button>
          <button class="reload" @click=${this.reloadMap} title="Reload current map">↻</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'map-selector-panel': MapSelectorPanel;
  }
}
